import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import '../styles/LegalPages.css';

const TermsOfService = () => {
  return (
    <div className="legal-page">
      <SEO 
        title="Terms of Service - VibrantCast"
        description="VibrantCast Terms of Use - Read our terms and conditions for using the platform."
      />
      
      <nav className="legal-nav">
        <div className="legal-nav-container">
          <Link to="/" className="legal-logo">
            <img 
              src="/images/inline-logo-white-1.png" 
              alt="VibrantCast" 
              className="legal-logo-img"
            />
          </Link>
          <Link to="/login" className="legal-nav-btn">Sign In</Link>
        </div>
      </nav>

      <div className="legal-content">
        <div className="legal-container">
          <h1>VibrantCast, LLC Terms of Use</h1>
          
          <p className="legal-intro">
            Welcome to VibrantCast! These Terms of Use ("Terms") govern your access to and use of VibrantCast, LLC's services, including our website, mobile application, and related products and features (collectively, the "Platform"). By using VibrantCast, you agree to be bound by these Terms.
          </p>

          <section className="legal-section">
            <h2>1. Eligibility</h2>
            <p>
              VibrantCast is intended for users 13 years of age and older. By using the Platform, you confirm that you are at least 13 years old. If you are under the age of 18, you may only use VibrantCast with the consent and supervision of a parent or legal guardian, who agrees to these Terms on your behalf. VibrantCast does not knowingly collect information from children under 13.
            </p>
          </section>

          <section className="legal-section">
            <h2>2. Your Account</h2>
            <p>
              To access many features of VibrantCast, you must create an account. You are responsible for all activities that occur under your account and must maintain the confidentiality of your login credentials. Do not share your account with others.
            </p>
            <p>
              If you believe your account has been compromised, contact us immediately at <a href="mailto:support@vibrantcast.com">support@vibrantcast.com</a>.
            </p>
          </section>

          <section className="legal-section">
            <h2>3. User Content</h2>
            <p>
              You may post, upload, or share artwork, images, videos, audio stories, exhibition details, event listings, and other materials ("User Content") on VibrantCast.
            </p>
            <p>
              You retain ownership of your User Content. However, by uploading or posting content to VibrantCast, you grant us a non-exclusive, worldwide, royalty-free license to use, reproduce, distribute, display, modify, and promote that content for purposes including operating, promoting, and improving the Platform and our services.
            </p>
            <p>
              By using features that allow you to upload content from your device's camera or photo library, you grant VibrantCast permission to access these tools for the sole purpose of uploading and displaying your selected content within the Platform.
            </p>
            <p>You also allow us to:</p>
            <ul>
              <li>Display your content to other users.</li>
              <li>Resize or adapt content for technical needs.</li>
              <li>Include your work in promotional or educational materials.</li>
              <li>Enable galleries, collectors, or partners to view, interact with, or inquire about your content.</li>
            </ul>
            <p>
              You are solely responsible for your User Content. We reserve the right to remove or restrict any content that violates these Terms or our policies.
            </p>
            <p>
              Event listings and exhibition announcements you share on VibrantCast are considered User Content and subject to the same terms. You are responsible for ensuring any event details you post are accurate and that you have permission to promote the venue or collaborators mentioned.
            </p>
          </section>

          <section className="legal-section">
            <h2>4. Community Guidelines</h2>
            <p>
              All users must follow our <a href="https://info.vibrantcast.com/community-guidelines/" target="_blank" rel="noopener noreferrer">Community Guidelines</a> to help keep VibrantCast safe, respectful, and inspiring for everyone. Violations may result in content removal or account suspension.
            </p>
            <p>Prohibited behavior includes but is not limited to:</p>
            <ul>
              <li>Harassment, hate speech, or abusive content.</li>
              <li>Uploading content that infringes on the rights of others.</li>
              <li>Fraudulent or deceptive conduct.</li>
              <li>Use of the Platform for unlawful purposes.</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>5. Event Participation and Promotion</h2>
            <p>By participating in a VibrantCast-powered event (in-person or online), you agree that:</p>
            <ul>
              <li>VibrantCast may record, photograph, or livestream portions of the event.</li>
              <li>VibrantCast may use your likeness or artwork displayed at the event for promotional purposes, including social media, email campaigns, and future marketing materials.</li>
            </ul>
            <p>
              If you do not wish to be recorded or featured, you must notify us in writing prior to the event.
            </p>
          </section>

          <section className="legal-section">
            <h2>6. Artwork Inquiries</h2>
            <p>
              At this time, VibrantCast facilitates connections between artists and collectors through an inquiry-based system only. All transactions, payments, shipping, and other terms of sale are handled directly between the artist and the buyer.
            </p>
            <p>
              VibrantCast does not process payments, manage orders, or assume responsibility for fulfillment, shipping, or any issues that may arise during a sale.
            </p>
          </section>

          <section className="legal-section">
            <h2>7. Account Suspension and Termination</h2>
            <p>We may suspend or terminate your account without notice if:</p>
            <ul>
              <li>You violate these Terms or any associated policies.</li>
              <li>Your behavior poses a risk to VibrantCast, its users, or partners.</li>
              <li>We are required to do so by law.</li>
            </ul>
            <p>
              You may delete your account at any time via your account settings. Certain information may be retained for legal, audit, or operational reasons.
            </p>
          </section>

          <section className="legal-section">
            <h2>8. Third-Party Services</h2>
            <p>
              VibrantCast may integrate with or link to third-party services, such as payment processors or external gallery websites. We are not responsible for those services or their content.
            </p>
          </section>

          <section className="legal-section">
            <h2>9. Intellectual Property</h2>
            <p>
              All content and technology on VibrantCast (excluding User Content) is owned by VibrantCast, LLC or its licensors. You may not copy, modify, or distribute any part of the Platform without written permission.
            </p>
          </section>

          <section className="legal-section">
            <h2>10. Warranty Disclaimer</h2>
            <p>
              VibrantCast is provided on an "as is" basis without warranties of any kind. We do not guarantee that the Platform will be error-free, secure, or always available. You use the Platform at your own risk.
            </p>
          </section>

          <section className="legal-section">
            <h2>11. Limitation of Liability</h2>
            <p>
              To the fullest extent allowed by law, VibrantCast, LLC is not liable for any indirect, incidental, special, or consequential damages arising out of or related to your use of the Platform. Our total liability for any claim shall not exceed $100.
            </p>
          </section>

          <section className="legal-section">
            <h2>12. Indemnification</h2>
            <p>
              You agree to indemnify and hold harmless VibrantCast, LLC and its officers, directors, employees, and affiliates from any claims, liabilities, or expenses (including legal fees) arising out of your use of the Platform, your User Content, or your violation of these Terms.
            </p>
          </section>

          <section className="legal-section">
            <h2>13. Governing Law and Venue</h2>
            <p>
              These Terms are governed by the laws of the State of Delaware, without regard to its conflict of laws rules. Any legal action shall be brought in the courts of Delaware, unless arbitration is required.
            </p>
          </section>

          <section className="legal-section">
            <h2>14. Dispute Resolution and Arbitration</h2>
            <p>
              If you have a dispute with us, you agree to try to resolve it informally first by contacting us. If a resolution is not reached within 60 days, both parties agree to resolve the matter through binding individual arbitration under the rules of the American Arbitration Association. You agree to waive any right to participate in class actions or class-wide arbitration.
            </p>
          </section>

          <section className="legal-section">
            <h2>15. Changes to These Terms</h2>
            <p>
              We may update these Terms from time to time. If we do, we'll notify you through the Platform or via email. Continued use of VibrantCast after changes means you accept the new Terms.
            </p>
          </section>

          <section className="legal-section">
            <h2>16. Contact Us</h2>
            <p>
              If you have any questions, concerns, or feedback regarding these Terms, please reach out to us at:
            </p>
            <p className="legal-contact">
              📧 <a href="mailto:support@vibrantcast.com">support@vibrantcast.com</a>
            </p>
          </section>
        </div>
      </div>

      <footer className="legal-footer">
        <div className="legal-footer-container">
          <p>© {new Date().getFullYear()} VibrantCast, LLC. All rights reserved.</p>
          <div className="legal-footer-links">
            <Link to="/terms">Terms of Service</Link>
            <Link to="/privacy">Privacy Policy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TermsOfService;
